import { publicSectionPath, TripOverviewSection } from '@components/TripOverview/core'
import React from 'react'
import PublicItineraryPage from '../PublicItinerary/PublicItineraryPage'
import PublicExpensesPage from './PublicExpensesPage'
import PublicFilesPage from './PublicFilesPage'
import PublicFlightDetailPage from './PublicFlightDetailPage'
import PublicFlightsPage from './PublicFlightsPage'
import PublicIdeasPage from './PublicIdeasPage'
import PublicAccommodationDetailPage from './PublicAccommodationDetailPage'
import PublicAccommodationsPage from './PublicAccommodationsPage'

/**
 * The registry App.tsx maps into `<Route>`s for every public per-section page.
 * Paths compose from `publicSectionPath` so routes, share links, and tile
 * deep-links cannot drift. Typed `Record<TripOverviewSection, …>`, so a new
 * section fails the build here until it has a page.
 */
export const PUBLIC_SECTION_ROUTES: Record<
	TripOverviewSection,
	{ path: string; Component: React.ComponentType }
> = {
	itinerary: { path: publicSectionPath(':tripCode', 'itinerary'), Component: PublicItineraryPage },
	ideas: { path: publicSectionPath(':tripCode', 'ideas'), Component: PublicIdeasPage },
	accommodations: { path: publicSectionPath(':tripCode', 'accommodations'), Component: PublicAccommodationsPage },
	flights: { path: publicSectionPath(':tripCode', 'flights'), Component: PublicFlightsPage },
	files: { path: publicSectionPath(':tripCode', 'files'), Component: PublicFilesPage },
	money: { path: publicSectionPath(':tripCode', 'money'), Component: PublicExpensesPage },
}

// Named aliases for tests and any surface that needs a single pattern.
export const PUBLIC_ITINERARY_PATH = PUBLIC_SECTION_ROUTES.itinerary.path
export const PUBLIC_IDEAS_PATH = PUBLIC_SECTION_ROUTES.ideas.path
export const PUBLIC_ACCOMMODATIONS_PATH = PUBLIC_SECTION_ROUTES.accommodations.path
export const PUBLIC_FLIGHTS_PATH = PUBLIC_SECTION_ROUTES.flights.path
export const PUBLIC_EXPENSES_PATH = PUBLIC_SECTION_ROUTES.money.path
export const PUBLIC_FILES_PATH = PUBLIC_SECTION_ROUTES.files.path

/** Read-only stay detail under the list (`…/accommodations/:accommodationId`). */
export const publicAccommodationDetailPath = (tripCode: string, accommodationId: number | string): string =>
	`${publicSectionPath(tripCode, 'accommodations')}/${accommodationId}`

/** Read-only flight detail under the flights list (`…/flights/:flightGroupId`). */
export const publicFlightDetailPath = (tripCode: string, flightGroupId: number | string): string =>
	`${publicSectionPath(tripCode, 'flights')}/${flightGroupId}`

/**
 * Per-item detail pages nested under a section list. Kept out of
 * `PUBLIC_SECTION_ROUTES`, which is one route per section by construction.
 */
export const PUBLIC_DETAIL_ROUTES: ReadonlyArray<{
	key: string
	path: string
	Component: React.ComponentType
}> = [
	{
		key: 'accommodation-detail',
		path: publicAccommodationDetailPath(':tripCode', ':accommodationId'),
		Component: PublicAccommodationDetailPage,
	},
	{
		key: 'flight-detail',
		path: publicFlightDetailPath(':tripCode', ':flightGroupId'),
		Component: PublicFlightDetailPage,
	},
]
